import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API, BACKEND_URL } from '../App';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { CheckCircle2, Users, MessageCircle, Calendar, Plug, Stethoscope, AlertTriangle } from 'lucide-react';
import CircleFeedTab from '../components/circle/CircleFeedTab';
import CircleTasksTab from '../components/circle/CircleTasksTab';
import CircleMembersTab from '../components/circle/CircleMembersTab';
import EditCircleDialog from '../components/circle/EditCircleDialog';
import DeleteCircleDialog from '../components/circle/DeleteCircleDialog';
import AppLogo from '../components/AppLogo';
const CircleDetail = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [circle, setCircle] = useState(null);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'feed');

  const [newPost, setNewPost] = useState({ content: '', post_type: 'update' });
  const [newTask, setNewTask] = useState({ title: '', description: '', assigned_to: '', due_date: '' });
  const [newMember, setNewMember] = useState({ user_email: '', circle_role: 'supporter' });
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [editCircleDialogOpen, setEditCircleDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editCircleData, setEditCircleData] = useState({
    name: '', description: '', venmo_username: '', cashapp_username: '',
    zelle_info: '', gofundme_link: '', caringbridge_link: '', resource_categories: []
  });
  const [newCategory, setNewCategory] = useState({ value: '', label: '' });
  const [currentUserRingLevel, setCurrentUserRingLevel] = useState(null);
  const [postImages, setPostImages] = useState([]);
  const [uploadingPostImage, setUploadingPostImage] = useState(false);

  useEffect(() => { fetchCircleData(); }, [circleId]);

  useEffect(() => {
    if (circle) {
      setEditCircleData({
        name: circle.name, description: circle.description || '',
        venmo_username: circle.venmo_username || '', cashapp_username: circle.cashapp_username || '',
        zelle_info: circle.zelle_info || '', gofundme_link: circle.gofundme_link || '',
        caringbridge_link: circle.caringbridge_link || '', resource_categories: circle.resource_categories || []
      });
    }
  }, [circle]);

  useEffect(() => {
    if (location.state?.openEditDialog && circle) {
      setEditCircleDialogOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, circle]);

  const fetchCircleData = async () => {
    try {
      const [circleRes, membersRes] = await Promise.all([
        axios.get(`${API}/circles/${circleId}`),
        axios.get(`${API}/circles/${circleId}/members`),
      ]);
      setCircle(circleRes.data);
      setMembers(membersRes.data);
      // Posts and tasks may not exist in new coordination API — load separately
      try { const postsRes = await axios.get(`${API}/circles/${circleId}/posts`); setPosts(postsRes.data); } catch (e) { setPosts([]); }
      try { const tasksRes = await axios.get(`${API}/circles/${circleId}/tasks`); setTasks(tasksRes.data); } catch (e) { setTasks([]); }
      const currentMember = membersRes.data.find(m => m.user_id === user.id);
      if (currentMember) setCurrentUserRingLevel(currentMember.circle_role || currentMember.ring_level);
    } catch (error) {
      toast.error('Failed to fetch circle data');
      navigate('/circles');
    } finally { setLoading(false); }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.content.trim()) return;
    try {
      const response = await axios.post(
        `${API}/posts`,
        { ...newPost, circle_id: circleId, image_urls: postImages.length > 0 ? postImages : null }
      );
      setPosts([response.data, ...posts]);
      setNewPost({ content: '', post_type: 'update' });
      setPostImages([]);
      toast.success('Post shared!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create post'); }
  };

  const handlePostImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPostImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPostImages(prev => [...prev, res.data.path]);
      toast.success('Photo attached!');
    } catch (error) { toast.error('Upload failed'); }
    finally { setUploadingPostImage(false); }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/tasks`, { ...newTask, circle_id: circleId }
      );
      setTasks([...tasks, response.data]);
      setNewTask({ title: '', description: '', assigned_to: '', due_date: '' });
      setAddTaskDialogOpen(false);
      toast.success('Task created!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create task'); }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/invitations`,
        { circle_id: circleId, invited_email: newMember.user_email, circle_role: newMember.circle_role }
      );
      setNewMember({ user_email: '', circle_role: 'supporter' });
      setAddMemberDialogOpen(false);
      toast.success('Invitation sent! They will be notified to join the circle.');
      fetchCircleData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to send invitation'); }
  };

  const getNextStatus = (status) => {
    if (status === 'pending') return 'in_progress';
    if (status === 'in_progress') return 'completed';
    return 'pending';
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    try {
      const nextStatus = getNextStatus(currentStatus);
      await axios.put(`${API}/tasks/${taskId}`, { status: nextStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: nextStatus } : t));
      const statusMessages = { completed: 'Marked as done - great work!', in_progress: 'On it! Moved to In Progress.', pending: 'Moved back to To Do.' };
      toast.success(statusMessages[nextStatus]);
    } catch (error) { toast.error('Failed to update task'); }
  };

  const handleClaimTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/claim`);
      toast.success("Thank you for stepping up! You're making a difference.");
      fetchCircleData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to claim task'); }
  };

  const handleAddCategory = () => {
    if (!newCategory.label || !newCategory.value) { toast.error('Please fill in both fields'); return; }
    const categories = editCircleData.resource_categories || [];
    setEditCircleData({ ...editCircleData, resource_categories: [...categories, { ...newCategory }] });
    setNewCategory({ value: '', label: '' });
  };

  const handleRemoveCategory = (index) => {
    const categories = [...editCircleData.resource_categories];
    categories.splice(index, 1);
    setEditCircleData({ ...editCircleData, resource_categories: categories });
  };

  const handleEditCircle = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/circles/${circleId}`, editCircleData, { params: editCircleData });
      setCircle({ ...circle, ...editCircleData });
      setEditCircleDialogOpen(false);
      toast.success('Circle updated successfully!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update circle'); }
  };

  const handleDeleteCircle = async () => {
    try {
      await axios.delete(`${API}/circles/${circleId}`);
      toast.success('Circle deleted successfully');
      navigate('/circles');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete circle'); }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(`${API}/members/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success('Member removed successfully');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to remove member'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="circle-detail">
      <div className="surface-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppLogo />
              <div>
                <h1 className="text-2xl font-bold text-foreground">{circle?.name}</h1>
                {circle?.description && <p className="text-sm text-muted-foreground">{circle.description}</p>}
              </div>
            </div>

            {circle?.created_by === user.id && (
              <div className="flex items-center gap-2">
                <EditCircleDialog
                  open={editCircleDialogOpen} onOpenChange={setEditCircleDialogOpen}
                  data={editCircleData} setData={setEditCircleData} onSubmit={handleEditCircle}
                  newCategory={newCategory} setNewCategory={setNewCategory}
                  onAddCategory={handleAddCategory} onRemoveCategory={handleRemoveCategory}
                />
                <DeleteCircleDialog
                  open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}
                  onDelete={handleDeleteCircle}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
            <TabsList className="grid w-full grid-cols-3 max-w-md">
              <TabsTrigger value="feed" data-testid="feed-tab">
                <MessageCircle className="w-4 h-4 mr-2" /> Feed
              </TabsTrigger>
              <TabsTrigger value="tasks" data-testid="tasks-tab">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Tasks
              </TabsTrigger>
              <TabsTrigger value="members" data-testid="members-tab">
                <Users className="w-4 h-4 mr-2" /> Members
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex gap-2 ml-4">
            {currentUserRingLevel && ['inner', 'primary'].includes(currentUserRingLevel) && (
              <Button variant="outline" onClick={() => navigate(`/circles/${circleId}/emergency-plan`)} className="text-destructive hover:text-destructive border-destructive/20 hover:border-destructive/30" data-testid="emergency-plan-btn">
                <AlertTriangle className="w-4 h-4 mr-2" /> Emergency
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/circles/${circleId}/calendar`)} data-testid="calendar-btn">
              <Calendar className="w-4 h-4 mr-2" /> Calendar
            </Button>
            <Button variant="outline" onClick={() => navigate(`/circles/${circleId}/medical-providers`)} data-testid="medical-providers-btn">
              <Stethoscope className="w-4 h-4 mr-2" /> Providers
            </Button>
            <Button variant="outline" onClick={() => navigate(`/circles/${circleId}/integrations`)} data-testid="integrations-btn">
              <Plug className="w-4 h-4 mr-2" /> Integrations
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="hidden"><TabsList /></div>

          <TabsContent value="feed" className="space-y-6">
            <CircleFeedTab
              posts={posts} newPost={newPost} setNewPost={setNewPost}
              handleCreatePost={handleCreatePost} postImages={postImages}
              setPostImages={setPostImages} handlePostImageUpload={handlePostImageUpload}
              uploadingPostImage={uploadingPostImage} BACKEND_URL={BACKEND_URL}
            />
          </TabsContent>

          <TabsContent value="tasks" className="space-y-6">
            <CircleTasksTab
              tasks={tasks} members={members} user={user}
              newTask={newTask} setNewTask={setNewTask}
              handleCreateTask={handleCreateTask} handleToggleTask={handleToggleTask}
              handleClaimTask={handleClaimTask} addTaskDialogOpen={addTaskDialogOpen}
              setAddTaskDialogOpen={setAddTaskDialogOpen}
            />
          </TabsContent>

          <TabsContent value="members" className="space-y-6">
            <CircleMembersTab
              members={members} user={user} circle={circle}
              newMember={newMember} setNewMember={setNewMember}
              handleAddMember={handleAddMember} handleRemoveMember={handleRemoveMember}
              addMemberDialogOpen={addMemberDialogOpen} setAddMemberDialogOpen={setAddMemberDialogOpen}
              onMembersChanged={fetchCircleData}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CircleDetail;
